import { useState } from 'react';
import { Check, Pencil, Plus, Search, Target, Trash2, X } from 'lucide-react';
import {
  filterGoals,
  formatGoalDate,
  goalProgressLabel,
  isGoalComplete,
  liveChecklist
} from './lib/goals';

const emptyDraft = () => ({
  title: '',
  notes: '',
  targetDate: '',
  steps: []
});

const fieldClass = 'w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2.5 text-sm text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500';

const statusOptions = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'done', label: 'Done' }
];

// The parent owns the map and the Firebase write. Checks and new steps save
// immediately. Title, notes, and the target date save when the form is submitted.
export default function GoalsTab({
  goals = [],
  onSave,
  onToggleItem,
  onAddItem,
  onRemoveItem,
  onDelete,
  syncLine = null
}) {
  const [draft, setDraft] = useState(emptyDraft);
  const [starter, setStarter] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [shell, setShell] = useState({ title: '', notes: '', targetDate: '' });
  const [shellSaving, setShellSaving] = useState(false);
  const [stepDrafts, setStepDrafts] = useState({});
  const [confirmId, setConfirmId] = useState(null);

  const canSave = draft.title.trim() && !saving;
  const filtered = filterGoals(goals, { query, status });
  const openCount = goals.filter((goal) => !isGoalComplete(goal)).length;
  const doneCount = goals.length - openCount;

  const setDraftField = (key) => (event) => {
    setDraft((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const addStarterStep = () => {
    const text = starter.trim();
    if (!text) return;
    setDraft((prev) => ({ ...prev, steps: [...prev.steps, text] }));
    setStarter('');
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const ok = await onSave({
        title: draft.title,
        notes: draft.notes,
        targetDate: draft.targetDate,
        items: draft.steps.map((text) => ({ text }))
      });
      if (ok) {
        setDraft(emptyDraft());
        setStarter('');
      }
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (goal) => {
    setEditingId(goal.id);
    setShell({ title: goal.title, notes: goal.notes || '', targetDate: goal.targetDate || '' });
    setConfirmId(null);
  };

  const saveShell = async (event) => {
    event.preventDefault();
    if (!editingId || !shell.title.trim() || shellSaving) return;
    setShellSaving(true);
    try {
      const ok = await onSave({
        id: editingId,
        title: shell.title,
        notes: shell.notes,
        targetDate: shell.targetDate
      });
      if (ok) setEditingId(null);
    } finally {
      setShellSaving(false);
    }
  };

  const addStep = async (goalId) => {
    const text = (stepDrafts[goalId] || '').trim();
    if (!text) return;
    setStepDrafts((prev) => ({ ...prev, [goalId]: '' }));
    const ok = await onAddItem(goalId, text);
    if (!ok) setStepDrafts((prev) => ({ ...prev, [goalId]: text }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
          <Target className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-zinc-800">Goals</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">
          Store a goal, then check off each step as you finish it.
        </p>
      </div>

      <form onSubmit={handleCreate} className="space-y-4 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-5">
        <h3 className="text-sm font-bold text-zinc-800">Add a goal</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-bold text-zinc-700">Goal</span>
            <input
              value={draft.title}
              onChange={setDraftField('title')}
              placeholder="Ship the pricing page"
              className={fieldClass}
              maxLength={200}
              autoComplete="off"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold text-zinc-700">Target date <span className="font-medium text-zinc-400">(optional)</span></span>
            <input
              type="date"
              value={draft.targetDate}
              onChange={setDraftField('targetDate')}
              className={fieldClass}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-bold text-zinc-700">Notes <span className="font-medium text-zinc-400">(optional)</span></span>
            <textarea
              value={draft.notes}
              onChange={setDraftField('notes')}
              placeholder="What finished looks like."
              rows={2}
              maxLength={2000}
              className={`${fieldClass} resize-none`}
            />
          </label>
        </div>

        <div className="space-y-2">
          <span className="block text-xs font-bold text-zinc-700">Steps <span className="font-medium text-zinc-400">(optional)</span></span>
          {draft.steps.length > 0 && (
            <ul className="space-y-1.5">
              {draft.steps.map((text, index) => (
                <li key={`${text}-${index}`} className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2">
                  <Check className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                  <span className="min-w-0 flex-1 text-sm text-zinc-700">{text}</span>
                  <button
                    type="button"
                    onClick={() => setDraft((prev) => ({
                      ...prev,
                      steps: prev.steps.filter((_, stepIndex) => stepIndex !== index)
                    }))}
                    className="rounded-lg p-1 text-zinc-400 hover:bg-white hover:text-red-500"
                    aria-label={`Remove starter step ${text}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <input
              value={starter}
              onChange={(event) => setStarter(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addStarterStep();
                }
              }}
              placeholder="Add a step"
              maxLength={300}
              autoComplete="off"
              className={fieldClass}
            />
            <button
              type="button"
              onClick={addStarterStep}
              disabled={!starter.trim()}
              className="inline-flex shrink-0 items-center gap-1 rounded-xl border border-zinc-200 bg-white px-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {syncLine}
          <button
            type="submit"
            disabled={!canSave}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-600 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            {saving ? 'Saving…' : 'Add goal'}
          </button>
        </div>
      </form>

      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-semibold text-zinc-500">
            {goals.length === 0
              ? 'No goals yet'
              : `${goals.length} ${goals.length === 1 ? 'goal' : 'goals'} · ${openCount} open · ${doneCount} done`}
            {goals.length > 0 && (query.trim() || status !== 'all') ? ` · ${filtered.length} matching` : ''}
            {goals.length > 0 ? ' · showing all' : ''}
          </p>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="flex rounded-xl border border-zinc-200 bg-zinc-50 p-0.5">
              {statusOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setStatus(option.id)}
                  className={`rounded-lg px-2.5 py-1 text-[11px] font-bold ${
                    status === option.id ? 'bg-white text-indigo-700 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="relative w-full sm:w-56">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search goals or steps"
                className="w-full rounded-xl border border-zinc-200 bg-zinc-50 py-2 pl-8 pr-3 text-sm text-zinc-800 placeholder-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
          </div>
        </div>

        {goals.length === 0 && (
          <div className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 px-4 py-8 text-center">
            <p className="text-sm font-semibold text-zinc-700">Your first goal</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-zinc-500">
              Name the goal, add the steps, and check each one off when it is done.
            </p>
          </div>
        )}

        {goals.length > 0 && filtered.length === 0 && (
          <p className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500">
            Nothing matches that filter.
          </p>
        )}

        {filtered.map((goal) => {
          const steps = liveChecklist(goal);
          const complete = isGoalComplete(goal);
          const editing = editingId === goal.id;
          return (
            <section
              key={goal.id}
              className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${
                complete ? 'border-emerald-200' : 'border-zinc-200'
              }`}
            >
              <header className="flex items-start justify-between gap-3 border-b border-zinc-100 px-4 py-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-bold text-zinc-900">{goal.title}</h3>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                      complete ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'
                    }`}
                    >
                      {goalProgressLabel(goal)}
                    </span>
                  </div>
                  {goal.targetDate && (
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                      Target {formatGoalDate(goal.targetDate)}
                    </p>
                  )}
                  {goal.notes && !editing && (
                    <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-500">{goal.notes}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => (editing ? setEditingId(null) : startEdit(goal))}
                    className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-100 hover:text-indigo-600"
                    aria-label={`Edit ${goal.title}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  {confirmId === goal.id ? (
                    <button
                      type="button"
                      onClick={() => onDelete(goal.id)}
                      className="rounded-lg bg-red-50 px-2 py-1.5 text-[11px] font-bold text-red-600 hover:bg-red-100"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmId(goal.id)}
                      className="rounded-lg p-2 text-zinc-400 hover:bg-red-50 hover:text-red-500"
                      aria-label={`Remove ${goal.title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </header>

              {editing && (
                <form onSubmit={saveShell} className="space-y-3 border-b border-zinc-100 bg-zinc-50 px-4 py-3">
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-bold text-zinc-700">Goal</span>
                    <input
                      value={shell.title}
                      onChange={(event) => setShell((prev) => ({ ...prev, title: event.target.value }))}
                      className={fieldClass}
                      maxLength={200}
                      autoComplete="off"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-bold text-zinc-700">Target date</span>
                    <input
                      type="date"
                      value={shell.targetDate}
                      onChange={(event) => setShell((prev) => ({ ...prev, targetDate: event.target.value }))}
                      className={fieldClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-bold text-zinc-700">Notes</span>
                    <textarea
                      value={shell.notes}
                      onChange={(event) => setShell((prev) => ({ ...prev, notes: event.target.value }))}
                      rows={2}
                      maxLength={2000}
                      className={`${fieldClass} resize-none`}
                    />
                  </label>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="rounded-xl px-3 py-2 text-xs font-semibold text-zinc-500 hover:text-zinc-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!shell.title.trim() || shellSaving}
                      className="rounded-xl bg-indigo-500 px-3 py-2 text-xs font-bold text-white hover:bg-indigo-600 disabled:opacity-50"
                    >
                      {shellSaving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </form>
              )}

              <ul className="divide-y divide-zinc-100">
                {steps.map((item) => (
                  <li key={item.id} className="flex items-start gap-2 px-4 py-2.5">
                    <label className="flex min-w-0 flex-1 items-start gap-3">
                      <input
                        type="checkbox"
                        checked={item.done}
                        onChange={() => onToggleItem(goal.id, item.id, !item.done)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-indigo-600"
                        aria-label={item.text}
                      />
                      <span className={`text-sm ${item.done ? 'text-zinc-400 line-through' : 'text-zinc-800'}`}>
                        {item.text}
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => onRemoveItem(goal.id, item.id)}
                      className="rounded-lg p-1.5 text-zinc-300 hover:bg-red-50 hover:text-red-500"
                      aria-label={`Remove step ${item.text}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>

              {steps.length === 0 && (
                <p className="px-4 py-3 text-sm text-zinc-400">Add a step to track completion.</p>
              )}

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  addStep(goal.id);
                }}
                className="flex gap-2 border-t border-zinc-100 px-4 py-3"
              >
                <input
                  value={stepDrafts[goal.id] || ''}
                  onChange={(event) => setStepDrafts((prev) => ({ ...prev, [goal.id]: event.target.value }))}
                  placeholder="Add a step"
                  maxLength={300}
                  autoComplete="off"
                  className={fieldClass}
                />
                <button
                  type="submit"
                  disabled={!(stepDrafts[goal.id] || '').trim()}
                  className="inline-flex shrink-0 items-center gap-1 rounded-xl border border-zinc-200 bg-white px-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" /> Add
                </button>
              </form>
            </section>
          );
        })}
      </div>
    </div>
  );
}
